// src/messagerie/pages/MessagingPatient.jsx
"use client";

import React, { useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";

import AppLayout from "../../navigation/Layout/Layout";
import { SocketProvider } from "../../messagerie/context/SocketContext";
import {
  MessagingProvider,
  useMessaging as useMessagingContext,
} from "../context/MessagingContext";

import ContactsList from "../components/ContactsList/ContactsList";
import ChatWindow from "../components/ChatWindow/ChatWindow";
import ProfileSidebar from "../components/ProfileSidebar/ProfileSidebar";

import { selectEffectiveUserId } from "../../sous-profile/redux/selectors/sousProfileSelectors";
import { fetchDoctors } from "../../authDoctor/redux/thunks/doctorsThunks";

import "./Messaging.css";

/* ─────────────────────────────────────────────
   Helpers
───────────────────────────────────────────── */
const normId = (v) => {
  if (v == null) return null;
  let s = String(v).trim();
  if (!s) return null;
  if (s.startsWith("DR_") || s.startsWith("dr_")) s = s.slice(3);
  if (s.startsWith("PAT_") || s.startsWith("pat_")) s = s.slice(4);
  s = s.trim();
  if (
    !s ||
    s.toLowerCase() === "nan" ||
    s.toLowerCase() === "undefined" ||
    s.toLowerCase() === "null"
  ) {
    return null;
  }
  return s;
};

const FALLBACK_NAMES = ["doctor", "unknown", "unknown user", "—", ""];

const isFallbackName = (name) => {
  if (!name) return true;
  return FALLBACK_NAMES.includes(String(name).trim().toLowerCase());
};

const buildMinimalDoctorContact = (doctorId, displayName = null) => {
  const id = normId(doctorId);
  if (!id) return null;
  return {
    id,
    userId: id,
    peerId: id,
    name: displayName,
    fullName: displayName,
    avatar: "https://cdn-icons-png.flaticon.com/512/149/149071.png",
    photo: "https://cdn-icons-png.flaticon.com/512/149/149071.png",
    meta: {
      type: "doctor",
      role: "doctor",
      doctorId: id,
      registerId: id,
      displayName,
    },
  };
};

const findDoctorName = (doctors, doctorId) => {
  if (!Array.isArray(doctors) || !doctorId) return null;
  const found = doctors.find((doc) => {
    const docId = normId(
      doc?.externalId ??
        doc?.doctorId ??
        doc?.medecinId ??
        doc?.id ??
        doc?._id
    );
    return docId && docId === doctorId;
  });
  if (!found) return null;
  return (
    found.fullName ||
    `${found.firstName || ""} ${found.lastName || ""}`.trim() ||
    found.name ||
    null
  );
};

/* ─────────────────────────────────────────────
   Hook mobile
───────────────────────────────────────────── */
function useIsMobile(maxWidth = 768) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= maxWidth;
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= maxWidth);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [maxWidth]);

  return isMobile;
}

/* ─────────────────────────────────────────────
   Corps de la page
   isMobile est reçu en prop depuis Messaging()
───────────────────────────────────────────── */
function MessagingBody({ isMobile }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const doctors = useSelector((state) => state.doctor?.doctors || []);
  const doctorsLoading = useSelector((state) => state.doctor?.loading || false);
  const doctorsLoaded = useSelector((state) => state.doctor?.doctorsLoaded || false);

  const {
    selectedContact,
    profileCollapsed,
    selectContact,
    toggleProfile,
    sharedMessages,
  } = useMessagingContext();

  /* Charger les médecins si pas encore chargés */
  useEffect(() => {
    if (!doctors.length && !doctorsLoading && !doctorsLoaded) {
      dispatch(fetchDoctors());
    }
  }, [dispatch, doctors.length, doctorsLoading, doctorsLoaded]);

  /* Sélectionner le contact depuis l'URL (?doctorId=...) */
  useEffect(() => {
    const sp = new URLSearchParams(location.search || "");
    const doctorId = normId(sp.get("doctorId"));
    if (!doctorId) return;

    const current = normId(
      selectedContact?.id ||
        selectedContact?.userId ||
        selectedContact?.meta?.doctorId
    );
    const currentName = selectedContact?.fullName || selectedContact?.name;

    if (current && current === doctorId && !isFallbackName(currentName)) return;

    let contact = null;

    try {
      const raw = sessionStorage.getItem("pendingSelectedContact");
      if (raw) {
        const parsed = JSON.parse(raw);
        const did =
          normId(parsed?.meta?.doctorId) ||
          normId(parsed?.id) ||
          normId(parsed?.userId);
        if (did && did === doctorId && !isFallbackName(parsed?.fullName || parsed?.name)) {
          contact = parsed;
        }
      }
    } catch {
      // ignore
    }

    if (!contact) {
      const nameFromUrl = sp.get("doctorName")
        ? decodeURIComponent(sp.get("doctorName"))
        : null;
      const nameFromRedux = findDoctorName(doctors, doctorId);
      const resolvedName = nameFromUrl || nameFromRedux || null;
      contact = buildMinimalDoctorContact(doctorId, resolvedName);
    }

    if (contact) selectContact(contact);
  }, [location.search, doctors, selectedContact, selectContact]);

  /* Enrichir le nom du contact une fois les médecins chargés */
  useEffect(() => {
    if (!doctors.length) return;
    const sp = new URLSearchParams(location.search || "");
    const doctorId = normId(sp.get("doctorId"));
    if (!doctorId) return;

    const currentId = normId(
      selectedContact?.id ||
        selectedContact?.userId ||
        selectedContact?.meta?.doctorId
    );
    if (currentId !== doctorId) return;

    const currentName = selectedContact?.fullName || selectedContact?.name;
    if (!isFallbackName(currentName)) return;

    const realName = findDoctorName(doctors, doctorId);
    if (!realName) return;

    selectContact({
      ...selectedContact,
      name: realName,
      fullName: realName,
      meta: { ...selectedContact?.meta, displayName: realName },
    });
  }, [doctors, location.search, selectedContact, selectContact]);

  const mobileTitle =
    selectedContact?.fullName ||
    selectedContact?.name ||
    selectedContact?.email ||
    t("msg.defaults.conversation");

  const handleBackToList = () => {
    selectContact(null);
    navigate("/messaging", { replace: true });
  };

  /* ── RENDU MOBILE ── */
  if (isMobile) {
    return (
      <main className="msg-mobile-page">
        {!selectedContact ? (
          /* Liste des contacts */
          <section className="msg-mobile-list">
            <ContactsList
              mode="patient"
              onSelectContact={selectContact}
              selectedContactId={null}
            />
          </section>
        ) : (
          /* Fenêtre de conversation */
          <section className="msg-mobile-chat">
            <div className="msg-mobile-chat-header">
              <button
                type="button"
                className="msg-mobile-chat-back"
                onClick={handleBackToList}
                aria-label={t("msg.aria.back")}
              >
                ←
              </button>
              <div className="msg-mobile-chat-title">{mobileTitle}</div>
            </div>

            <div className="msg-mobile-chat-body">
              <ChatWindow selectedContact={selectedContact} />
            </div>
          </section>
        )}
      </main>
    );
  }

  /* ── RENDU DESKTOP ── */
  return (
    <main
      className={`msg-content ${selectedContact ? "msg--mobile-chat-open" : ""}`}
      role="main"
      aria-label={t("msg.aria.page")}
    >
      <div
        className={`msg-3col ${profileCollapsed ? "no-profile" : "with-profile"}`}
      >
        {/* Colonne contacts */}
        <div className="msg-contacts">
          <div className="msg-contacts__scroll">
            <ContactsList
              mode="patient"
              onSelectContact={selectContact}
              selectedContactId={
                selectedContact?.id ||
                selectedContact?.userId ||
                selectedContact?.doctorId ||
                null
              }
            />
          </div>
        </div>

        {/* Colonne chat */}
        <div className="msg-chat">
          {!selectedContact ? (
            <div className="msg-empty">
              <div className="msg-empty__inner">
                <h3 style={{ margin: 0 }}>{t("msg.empty.title")}</h3>
                <p style={{ marginTop: 8, color: "#64748b" }}>
                  {t("msg.empty.subtitle")}
                </p>
              </div>
            </div>
          ) : (
            <ChatWindow selectedContact={selectedContact} />
          )}
        </div>

        {/* Colonne profil */}
        {!profileCollapsed && selectedContact && (
          <div className="msg-profile">
            <div className="msg-profile__scroll">
              <ProfileSidebar
                contact={selectedContact}
                messages={sharedMessages}
                collapsed={profileCollapsed}
                onToggle={toggleProfile}
              />
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

/* ─────────────────────────────────────────────
   Composant racine
   → isMobile décidé ici pour conditionner AppLayout
───────────────────────────────────────────── */
export default function Messaging() {
  const effectiveUserId = useSelector(selectEffectiveUserId);
  const isMobile = useIsMobile(768); // ← remonté ici, une seule source de vérité

  const userId = useMemo(
    () =>
      effectiveUserId ||
      Cookies.get("id") ||
      Cookies.get("user_id") ||
      Cookies.get("userId") ||
      null,
    [effectiveUserId]
  );

  const token = useMemo(
    () => Cookies.get("accessToken") || Cookies.get("auth_token") || null,
    []
  );

  return (
    <SocketProvider userId={userId} token={token}>
      <MessagingProvider>
        {isMobile ? (
          /*
           * Sur mobile : on monte MessagingBody SANS AppLayout
           * pour éviter que le sidebar/navbar réduise l'espace
           * disponible et casse le mode plein-écran messenger.
           */
          <MessagingBody isMobile={true} />
        ) : (
          <AppLayout>
            <MessagingBody isMobile={false} />
          </AppLayout>
        )}
      </MessagingProvider>
    </SocketProvider>
  );
}